import { memo, useEffect, useState } from 'react'

import { DoNotDisturbOutlined, RefreshOutlined } from '@mui/icons-material'
import { Box, Button, Skeleton, Typography } from '@mui/material'
import { defaultExperienceHandlers } from '@nativewaves/exp-app-default-config'
import { ExperienceLoader } from '@nativewaves/exp-app-utilities'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { videoObjectBlobWatchRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object/Blobs/Blob'
import { videoObjectQueryKeys } from 'services/queryKeys'
import { getDrmCredentialsFn, getEnvType } from 'utils'

const WatchRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [error, setError] = useState(false)

  const { videoobjectId: videoId, blobId } = videoObjectBlobWatchRoute.useParams()

  const resolveQuery = useQuery({
    ...videoObjectQueryKeys.blobResolve({ videoId, blobId }),
    retry: false,
    retryOnMount: false,
    meta: {
      optOutOfGlobalErrorHandling: true,
    },
  })

  const { data, refetch, isLoading, isError, isFetched } = resolveQuery

  const drmCredentialsFn = getDrmCredentialsFn(data)

  useEffect(() => {
    if (isFetched && !isLoading) {
      setHasLoadedOnce(true)
      setError(isError)
    }
  }, [isError, isFetched, isLoading])

  if (isLoading && !hasLoadedOnce) {
    return <Skeleton sx={{ height: '100%' }} variant="rectangular" />
  }

  if (error && hasLoadedOnce) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          maxWidth: 400,
          gap: 2,
          mx: 'auto',
        }}
      >
        <DoNotDisturbOutlined fontSize="large" />
        <Typography variant="heading-md">{t('domain:Workspace.Video.resolveErrorTitle')}</Typography>
        <Typography>{t('domain:Workspace.Video.resolveError')}</Typography>
        <Button
          onClick={() => refetch()}
          loading={isLoading}
          disabled={isLoading}
          variant="outlined"
          startIcon={<RefreshOutlined />}
        >
          {t('domain:Workspace.Video.resolveErrorButton')}
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <ExperienceLoader
        drmCredentialsFn={drmCredentialsFn}
        expHandlers={defaultExperienceHandlers}
        envType={getEnvType()}
        configId="console"
        manifestId={data?.expManifestMeta?.expManifestId}
        variant="desktop"
        autoPlay
      />
    </Box>
  )
}

export const Watch = memo(WatchRaw)
